import { Branding, BrandNames } from "@hopper-b2b/types";
import dayjs from "dayjs";
import "dayjs/locale/en";
import LocalizedFormat from "dayjs/plugin/localizedFormat";
import i18next, { i18n } from "i18next";
import { FC, ReactNode } from "react";
import en_translations from "../locales/en/index";
import es_translations from "../locales/es/index";
import fr_translations from "../locales/fr/index";
import { I18nContext } from "./I18nContext";
import { I18nNamespace, Translation, TranslationLanguage } from "./types";
import { I18nextProvider, initReactI18next } from "react-i18next";

// This is the server-side version of the I18nProvider
// This does not contain the hooks that are used in the client-side version.
// Since this cannot access localStorage, it uses the localeParam to determine the language.

// const en_translations = {
//   ...en_seo,
//   ...en_hotels,
//   ...en_ui,
//   ...en_app,
// };

interface iI18nProvider {
  children: ReactNode;
  branding: Branding;
  defaultLng: string;
  localeParam?: string;
  tenantTranslation?: Translation;
  preferredCurrency?: string;
}

export type i18nContextWithPreferredCurrency = i18n & {
  preferredCurrency?: string;
};

export const I18nProviderSSR: FC<iI18nProvider> = ({
  children,
  localeParam,
  branding,
  tenantTranslation = {},
  preferredCurrency,
}: iI18nProvider) => {
  const lang = localeParam;

  const fallbackLang =
    branding.default && branding.default in TranslationLanguage
      ? branding.default
      : TranslationLanguage.en;

  const translationLanguage =
    lang && lang in TranslationLanguage
      ? TranslationLanguage[lang as TranslationLanguage]
      : fallbackLang;

  // Create a new i18n instance for SSR
  const i18nInstance = i18next.createInstance();

  i18nInstance
    .use(initReactI18next)
    .init({
      resources: {},
      lng: translationLanguage,
      fallbackLng: fallbackLang,
    })
    .catch((err) => {
      console.error("i18next init failed", err);
    });

  i18nInstance?.services?.formatter?.addCached(
    "daterange",
    (lng: string | undefined, options?: Intl.DateTimeFormatOptions) => {
      const format = new Intl.DateTimeFormat(lng, {
        month: "short",
        day: "numeric",
        ...options,
      });
      return (val: unknown) => {
        if (Array.isArray(val) && val.length >= 2) {
          const date1 = val[0] as Date;
          const date2 = val[1] as Date;
          return format.formatRange(date1, date2);
        }
        return "";
      };
    }
  );

  // Set the locale for dayjs
  dayjs.locale(translationLanguage);
  dayjs.extend(LocalizedFormat);

  // Deep merge the values in the namespace.
  const deep = true;
  // Overwrite the values in the namespace.
  const overwrite = true;

  function addResourceBundles(language: string) {
    //Brand namespace
    i18nInstance.addResourceBundle(
      language,
      I18nNamespace.brand,
      {
        ...(language in branding ? (branding[language] as BrandNames) : {}),
      },
      deep,
      overwrite
    );

    //Translation namespace
    const translation = (() => {
      switch (language as TranslationLanguage) {
        case TranslationLanguage.en:
          return en_translations;
        case TranslationLanguage.es:
          return es_translations;
        case TranslationLanguage.fr:
          return fr_translations;
        default:
          return en_translations;
      }
    })();
    i18nInstance.addResourceBundle(
      language,
      I18nNamespace.translation,
      translation,
      deep,
      overwrite
    );

    if (tenantTranslation[language as TranslationLanguage]) {
      i18nInstance.addResourceBundle(
        language,
        I18nNamespace.translation,
        tenantTranslation[language as TranslationLanguage],
        deep,
        overwrite
      );
    }
  }

  addResourceBundles(translationLanguage);
  // Load the resources from fallback language
  const fallbackLng = i18nInstance.options.fallbackLng
    ? typeof i18nInstance.options.fallbackLng === "string"
      ? i18nInstance.options.fallbackLng
      : (i18nInstance.options.fallbackLng as string[])[0]
    : undefined;
  if (!!fallbackLng && fallbackLng !== translationLanguage) {
    addResourceBundles(fallbackLng);
  }

  const context: i18nContextWithPreferredCurrency = {
    ...i18nInstance,
    preferredCurrency,
  };

  return (
    <I18nContext.Provider value={context}>
      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment */}
      <I18nextProvider i18n={i18nInstance as any}>{children}</I18nextProvider>
    </I18nContext.Provider>
  );
};
